import type React from "react"
import { useTranslation } from "react-i18next"
import { useState, useEffect, useMemo, useCallback } from "react"
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert,
  Chip,
} from "@mui/material"
import { 
  Close as CloseIcon, 
  ArrowBack as ArrowBackIcon,
  Videocam as VideocamIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material"
import EntityList from "../../components/EntityList"
import api from "../../services/api"
import HorseLoader from "../../components/loadingHorse"
import type { Recorder, Tenant } from "../../interfaces/index"
import { useIsMobile, useIsTablet } from "../../hooks/useMediaQuery"
import { useCryptoNavigation, useAppNavigation } from "../../App"

// Interface para propriedades do componente
interface RecorderListProps {
  tenant_id?: number
}

const RecorderList: React.FC<RecorderListProps> = ({ tenant_id }) => {
  const { t } = useTranslation()
  console.log("🔄 RecorderList render");
  
  const { navigateToPage } = useCryptoNavigation()
  const { getParam } = useAppNavigation()
  
  // Obtém tenant_id do parâmetro criptografado se não foi passado como prop
  const tenantIdFromParams = getParam('tenantId')
  const finalTenantId = useMemo(() => {
    return tenant_id || Number(tenantIdFromParams) || 1
  }, [tenant_id, tenantIdFromParams])

  const theme = useTheme()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))

  const [selectedRecorder, setSelectedRecorder] = useState<Recorder | null>(null)
  const [openDetailsDialog, setOpenDetailsDialog] = useState<boolean>(false)
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [loadingTenant, setLoadingTenant] = useState<boolean>(true)
  
  // Estados para controle de exclusão
  const [warningDialogOpen, setWarningDialogOpen] = useState<boolean>(false)
  const [warningMessage, setWarningMessage] = useState<string>("")
  const [associatedCamerasCount, setAssociatedCamerasCount] = useState<number>(0)
  const [recorderToDelete, setRecorderToDelete] = useState<Recorder | null>(null)

  console.log("📊 RecorderList state:", { finalTenantId, loadingTenant, tenant: tenant?.tenantName });

  // Memoizar funções de navegação
  const handleNew = useCallback(() => {
    console.log("🚀 Navegando para novo gravador");
    navigateToPage('gravadores', { mode: 'new', tenantId: String(finalTenantId) });
  }, [navigateToPage, finalTenantId]);

  const handleEdit = useCallback((recorderId: number | string) => {
    console.log("✏️ Navegando para editar gravador ID:", recorderId);
    navigateToPage('gravadores', { mode: 'edit', id: String(recorderId), tenantId: String(finalTenantId) });
  }, [navigateToPage, finalTenantId]);

  const handleBackToTenants = useCallback(() => {
    navigateToPage('proprietarios', { mode: 'list' })
  }, [navigateToPage]);

  // Função para navegar para câmeras do gravador
  const handleViewRecorderCameras = useCallback((recorderId: number) => {
    console.log("📹 Navegando para câmeras do gravador:", recorderId);
    navigateToPage('cameras', { 
      mode: 'list', 
      tenantId: String(finalTenantId),
      recorderId: String(recorderId)
    });
  }, [navigateToPage, finalTenantId]);

  const handleBackHome = () => {
    navigateToPage('dashboard')
  }

  // Carregar tenant apenas uma vez
  useEffect(() => {
    let mounted = true;
    console.log("🔍 RecorderList useEffect - buscando tenant");

    const fetchTenant = async () => {
      try {
        const response = await api.get(`/tenant/${finalTenantId}`)
        
        if (!mounted) return;

        // Processar a resposta para extrair o tenant
        let tenantData = null

        if (response.data && response.data.tenant) {
          tenantData = response.data.tenant
        } else if (response.data && typeof response.data === "object") {
          if (response.data.tenantId && response.data.tenantName) {
            tenantData = response.data
          }
        }

        console.log("✅ Tenant carregado:", tenantData?.tenantName);
        setTenant(tenantData)
      } catch (error) {
        console.error("❌ Erro ao carregar tenant:", error)
      } finally {
        if (mounted) {
          setLoadingTenant(false)
        }
      }
    }

    fetchTenant()

    return () => {
      mounted = false;
    }
  }, [finalTenantId])

  // ⭐ INTERCEPTOR DE API - Bloqueia DELETE de gravadores com câmeras
  useEffect(() => {
    console.log("🛡️ Instalando interceptor de API para gravadores...");
    
    const interceptor = api.interceptors.request.use(async (config) => {
      // Verificar se é uma requisição DELETE para recorder
      if (config.method === 'delete' && config.url?.includes('/recorder/')) {
        console.log("🔍 INTERCEPTADO: DELETE para recorder");
        
        // Extrair recorderId da URL
        const match = config.url.match(/\/recorder\/\d+\/(\d+)/);
        if (match) {
          const recorderId = parseInt(match[1]);
          console.log("📋 Verificando câmeras do gravador ID:", recorderId);
          
          try {
            // Buscar câmeras deste gravador
            const camerasResponse = await api.get(`/camera/${finalTenantId}`);
            
            let allCameras: any[] = [];
            if (Array.isArray(camerasResponse.data)) {
              allCameras = camerasResponse.data;
            } else if (camerasResponse.data?.cameras) {
              allCameras = camerasResponse.data.cameras;
            }
            
            // Filtrar câmeras deste gravador usando o campo cameraRecorder
            const camerasDoGravador = allCameras.filter((camera: any) => {
              return camera.cameraRecorder === recorderId || Number(camera.cameraRecorder) === Number(recorderId);
            });
            
            console.log(`📊 Câmeras encontradas: ${camerasDoGravador.length}`);
            
            // Se TEM câmeras, BLOQUEIA o request! ⭐
            if (camerasDoGravador.length > 0) {
              console.log("🚫 BLOQUEANDO DELETE - Gravador tem câmeras!");
              
              // Mostrar aviso no frontend
              setRecorderToDelete({ recorderId, recorderName: `Gravador #${recorderId}` } as Recorder);
              setAssociatedCamerasCount(camerasDoGravador.length);
              setWarningMessage(
                t("recorders.deleteBlockedWarning", { count: camerasDoGravador.length })
              );
              setWarningDialogOpen(true);
              
              // CANCELA O REQUEST! ⭐⭐⭐
              return Promise.reject({
                config,
                message: 'DELETE_BLOCKED_HAS_CAMERAS',
                camerasCount: camerasDoGravador.length,
                __CUSTOM_ERROR__: true,
                __SILENT__: true  // Não mostrar alert
              });
            }
            
            console.log("✅ Sem câmeras - permitindo DELETE");
          } catch (error: any) {
            // Se for o nosso erro customizado, propaga
            if (error.__CUSTOM_ERROR__) {
              return Promise.reject(error);
            }
            console.error("❌ Erro ao verificar câmeras:", error);
          }
        }
      }
      
      return config;
    });
    
    // Cleanup: remover interceptor quando componente desmontar
    return () => {
      console.log("🗑️ Removendo interceptor de API...");
      api.interceptors.request.eject(interceptor);
    };
  }, [finalTenantId]);

  // Memoizar handleShowDetails
  const handleShowDetails = useCallback((recorder: Recorder): void => {
    console.log("👁️ Mostrando detalhes do gravador:", recorder.recorderName);
    setSelectedRecorder(recorder);
    setOpenDetailsDialog(true);
  }, []);

  // Fechar modal de detalhes
  const handleCloseDetails = useCallback(() => {
    console.log("❌ Fechando modal de detalhes");
    setOpenDetailsDialog(false);
    setSelectedRecorder(null);
  }, []);

  // ⭐ FUNÇÃO COMPLETA DE EXCLUSÃO COM VALIDAÇÃO
  const handleDeleteWithValidation = useCallback(async (recorderId: number) => {
    try {
      console.log("🛑 VALIDAÇÃO INICIADA - Gravador ID:", recorderId);
      
      // Encontrar o recorder
      const recorder = selectedRecorder?.recorderId === recorderId 
        ? selectedRecorder 
        : { recorderId, recorderName: `Gravador #${recorderId}` } as Recorder;
      
      setRecorderToDelete(recorder);
      
      // 1️⃣ BUSCAR câmeras PRIMEIRO
      console.log("📹 Buscando câmeras do tenant:", finalTenantId);
      const camerasResponse = await api.get(`/camera/${finalTenantId}`);
      
      let allCameras: any[] = [];
      if (Array.isArray(camerasResponse.data)) {
        allCameras = camerasResponse.data;
      } else if (camerasResponse.data?.cameras && Array.isArray(camerasResponse.data.cameras)) {
        allCameras = camerasResponse.data.cameras;
      }
      
      console.log("📊 Total de câmeras:", allCameras.length);
      
      // 2️⃣ FILTRAR câmeras deste gravador
      const camerasDoGravador = allCameras.filter((camera: any) => {
        return camera.cameraRecorder === recorderId || Number(camera.cameraRecorder) === Number(recorderId);
      });
      
      console.log(`📹 Câmeras do gravador ${recorderId}:`, camerasDoGravador.length);
      
      // 3️⃣ Se TEM câmeras → BLOQUEIA! ⭐
      if (camerasDoGravador.length > 0) {
        console.log("🚫 BLOQUEADO! Gravador tem", camerasDoGravador.length, "câmera(s)");
        
        setAssociatedCamerasCount(camerasDoGravador.length);
        setWarningMessage(
          t("recorders.deleteBlockedWarning", { count: camerasDoGravador.length })
        );
        setWarningDialogOpen(true);
        
        // ⭐ LANÇA ERRO PARA IMPEDIR CONTINUAÇÃO - SEM ALERT!
        throw new Error(`BLOQUEADO_SILENCIOSO`);
      }
      
      // 4️⃣ Se NÃO tem câmeras → Exclui direto (sem confirmação adicional)
      console.log("✅ Sem câmeras! Excluindo...");
      
      // 5️⃣ Chama a API de exclusão
      console.log("🗑️ Excluindo no backend...");
      await api.delete(`/recorder/${finalTenantId}/${recorderId}`);
      
      console.log("✅ Gravador excluído com sucesso!");
      
      // Fechar dialog de detalhes se aberto
      if (openDetailsDialog) {
        setOpenDetailsDialog(false);
      }
      
      // Atualizar lista
      setRefreshTrigger((prev) => prev + 1);
      
    } catch (err: any) {
      // Se é erro de bloqueio, não faz nada (já mostrou o aviso)
      if (err.message === 'BLOQUEADO_SILENCIOSO' || err.message?.startsWith('BLOQUEADO:')) {
        console.log("⚠️ Exclusão bloqueada - aviso já exibido");
        throw err; // Re-lança para o EntityList saber que falhou
      }
      
      // Se é outro erro, mostra aviso
      console.error("❌ Erro ao excluir:", err);
      
      const errorMsg = err.response?.data?.message || err.message || t("recorders.errorGeneric");
      setWarningMessage(t("recorders.errorPrefix", { msg: errorMsg }));
      setWarningDialogOpen(true);
      
      throw err;
    }
  }, [finalTenantId, selectedRecorder, openDetailsDialog]);

  // ⭐ WRAPPER que INTERCEPTA a exclusão ANTES do EntityList
  const handleDeleteInterceptor = useCallback(async (recorderId: number): Promise<boolean> => {
    try {
      console.log("🛑 INTERCEPTANDO exclusão - Gravador ID:", recorderId);
      
      // Encontrar o recorder
      const recorder = selectedRecorder?.recorderId === recorderId 
        ? selectedRecorder 
        : { recorderId, recorderName: `Gravador #${recorderId}` } as Recorder;
      
      setRecorderToDelete(recorder);
      
      // 1️⃣ BUSCAR câmeras ANTES de qualquer coisa
      console.log("📹 Buscando câmeras ANTES de tentar excluir...");
      const camerasResponse = await api.get(`/camera/${finalTenantId}`);
      
      let allCameras: any[] = [];
      if (Array.isArray(camerasResponse.data)) {
        allCameras = camerasResponse.data;
      } else if (camerasResponse.data?.cameras && Array.isArray(camerasResponse.data.cameras)) {
        allCameras = camerasResponse.data.cameras;
      }
      
      console.log("📊 Total de câmeras encontradas:", allCameras.length);
      
      // 2️⃣ FILTRAR câmeras deste gravador
      const camerasDoGravador = allCameras.filter((camera: any) => {
        const cameraRecorderId = camera.recorderId || camera.recorder_id;
        return cameraRecorderId === recorderId;
      });
      
      console.log(`📹 Câmeras do gravador ${recorderId}:`, camerasDoGravador.length);
      
      // 3️⃣ Se TEM câmeras → BLOQUEIA TOTALMENTE! ⭐
      if (camerasDoGravador.length > 0) {
        console.log("🚫 BLOQUEADO! Este gravador tem", camerasDoGravador.length, "câmera(s)");
        
        setAssociatedCamerasCount(camerasDoGravador.length);
        setWarningMessage(
          t("recorders.deleteBlockedWarning", { count: camerasDoGravador.length })
        );
        setWarningDialogOpen(true);
        
        // ⭐ RETORNA FALSE - EntityList NÃO vai continuar!
        return false;
      }
      
      // 4️⃣ Se NÃO tem câmeras → PERMITE exclusão
      console.log("✅ Sem câmeras! Permitindo exclusão...");
      
      // ⭐ RETORNA TRUE - EntityList pode continuar!
      return true;
      
    } catch (err: any) {
      console.error("❌ Erro na verificação:", err);
      
      setWarningMessage(t("recorders.errorCheckCameras", { msg: err.message }));
      setWarningDialogOpen(true);
      
      // Em caso de erro, BLOQUEIA por segurança
      return false;
    }
  }, [finalTenantId, selectedRecorder]);

  const handleToggleStatus = useCallback(async (recorder: Recorder) => {
    const newStatus = recorder.recorderStatus === 1 ? 0 : 1
    try {
      await api.put(`/recorder/${finalTenantId}/${recorder.recorderId}`, { recorderStatus: newStatus })
      setSelectedRecorder((prev) => prev ? { ...prev, recorderStatus: newStatus } : prev)
      setRefreshTrigger((prev) => prev + 1)
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || t("recorders.toggleStatusError")
      setWarningMessage(`Erro: ${msg}`)
      setWarningDialogOpen(true)
    }
  }, [finalTenantId])

  // Memoizar handleDelete (compatibilidade)
  const handleDelete = useCallback(async (recorderId: number) => {
    // Chama a função completa com validação
    await handleDeleteWithValidation(recorderId);
  }, [handleDeleteWithValidation]);

  // Fechar dialog de aviso
  const handleCloseWarning = useCallback(() => {
    setWarningDialogOpen(false);
    setWarningMessage("");
    setAssociatedCamerasCount(0);
    setRecorderToDelete(null);
  }, []);

  // ✅ FUNÇÃO para obter o nome do tipo baseado no ID
  const getRecorderTypeName = useCallback((recorderType: number | undefined): string => {
    if (recorderType === 2) return 'Intelbras';
    if (recorderType === 1) return 'Hikvision';
    return 'Hikvision'; // Default
  }, []);

  // ✅ FUNÇÃO para obter a cor do chip baseado no tipo
  const getRecorderTypeColor = useCallback((recorderType: number | undefined): "primary" | "secondary" => {
    if (recorderType === 2) return 'secondary'; // Intelbras = roxo
    return 'primary'; // Hikvision = azul
  }, []);
  const getRecorderStatusLabel = useCallback((status: number | undefined): string => {
  return status === 1 ? t("recorders.statusActive") : t("recorders.statusInactive")
}, [])

const getRecorderStatusColor = useCallback((status: number | undefined): "success" | "error" => {
  return status === 1 ? "success" : "error"
}, [])

  // Memoizar colunas COM larguras responsivas e equilibradas
const columns = useMemo(() => [
  {
    id: "recorderName",
    label: t("fields.name"),
    minWidth: 100,
    align: "center" as const,
    width: '15%', // ✅ Percentual responsivo
  },
  {
  id: "recorderStatus",
  label: t("fields.status"),
  minWidth: 90,
  width: "10%",
  align: "center" as const,
  mobileHideLabel: true,
  format: (value: number | undefined) => (
    <Chip
      label={getRecorderStatusLabel(value)}
      color={getRecorderStatusColor(value)}
      size="small"
      variant="outlined"
    />
  ),
},
  // ✅ COLUNA Tipo de Gravador
  {
    id: "recorderType",
    label: t("fields.type"),
    minWidth: 90,
    width: '12%', // ✅ Percentual responsivo
    align: "center" as const,
    mobileHideLabel: true, // badge visível no mobile, sem rótulo
    format: (value: number | undefined, row: Recorder) => (
      <Chip
        label={getRecorderTypeName(value)}
        color={getRecorderTypeColor(value)}
        size="small"
        variant="outlined"
      />
    ),
  },
  {
    id: "recorderModel",
    label: t("fields.model"),
    minWidth: 90,
    width: '12%', // ✅ Percentual responsivo
    align: "center" as const,
    hide: isMobile || isTablet,
  },
  {
    id: "recorderChannels",
    label: t("fields.channels"),
    minWidth: 200,
    width: '25%', // ✅ Maior porque tem botão
    align: "center" as const,
    format: (value: number, row: Recorder) => (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: 1,
        flexWrap: isTablet ? 'wrap' : 'nowrap' // ✅ Quebra linha em tablet se necessário
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          <VideocamIcon sx={{ fontSize: 16, mr: 0.5 }} />
          {value}
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleViewRecorderCameras(row.recorderId);
          }}
          sx={{ 
            minWidth: 'auto', 
            px: 1,
            whiteSpace: 'nowrap',
            fontSize: isTablet ? '0.7rem' : '0.875rem' // ✅ Fonte menor em tablet
          }}
        >
          {t("recorders.viewCameras")}
        </Button>
      </Box>
    ),
  },
  {
    id: "recorderIntIP",
    label: t("recorders.intIP"),
    minWidth: 110,
    width: '18%', // ✅ Percentual responsivo
    hide: isMobile || isTablet,
  },
  {
    id: "recorderExtIP",
    label: t("recorders.extIP"),
    minWidth: 110,
    width: '18%', // ✅ Percentual responsivo
    hide: isMobile || isTablet,
  },
], [
  isMobile,
  isTablet,
  handleViewRecorderCameras,
  getRecorderTypeName,
  getRecorderTypeColor,
  getRecorderStatusLabel,
  getRecorderStatusColor,
]);
  // Memoizar transformData
  const transformData = useCallback((data: any): any[] => {
    console.log("🔄 transformData executando");
    
    let recorders: any[] = []

    if (Array.isArray(data)) {
      recorders = data
    } else if (data && data.recorders && Array.isArray(data.recorders)) {
      recorders = data.recorders
    } else if (data && typeof data === "object") {
      try {
        const values = Object.values(data)
        const arrayValues = values.filter((item) => Array.isArray(item))
        recorders = arrayValues.length > 0 ? arrayValues[0] : []
      } catch (error) {
        console.error("Erro ao extrair gravadores:", error)
        recorders = []
      }
    }

    console.log(`✅ ${recorders.length} gravadores transformados`);
    return recorders
  }, []);

  return (
    <>
      {/* Breadcrumbs */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: isMobile ? "100%" : "auto" }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ flexWrap: "wrap" }}>
              {loadingTenant ? (
                <CircularProgress size={20} />
              ) : (
                <Typography
                  color="text.primary"
                  sx={{
                    maxWidth: { xs: "150px", sm: "300px" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {tenant?.tenantName || t("recorders.defaultOwner")}
                </Typography>
              )}
            </Breadcrumbs>
          </Box>
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={handleBackHome}
            size={isMobile ? "small" : "medium"}
            sx={{ mt: isMobile ? 1 : 0 }}
          >
            {t("common.back")}
          </Button>
        </Box>
      </Paper>

      <EntityList
        title={t("nav.recorders")}
        endpoint={`/recorder/${finalTenantId}`}
        columns={columns}
        addPath=""
        editPathPrefix=""
        idField="recorderId"
        dataPath="recorders"
        onRowClick={handleShowDetails}
        refreshTrigger={refreshTrigger}
        transformData={transformData}
        useEncryptedNavigation={true}
        encryptedPageName="gravadores"
        onAddClick={handleNew}
        onEditClick={handleEdit}
        onDeleteClick={handleDeleteWithValidation}
        extraParams={{ tenantId: String(finalTenantId) }}
      />

      {/* Dialog de Aviso (Bloqueio ou Erro) */}
      <Dialog
        open={warningDialogOpen}
        onClose={handleCloseWarning}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          {associatedCamerasCount > 0 ? t("recorders.cannotDelete") : t("recorders.attention")}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            <Typography variant="body1">
              {warningMessage}
            </Typography>
            {associatedCamerasCount > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  {t("recorders.deleteBlockedTitle")}
                </Typography>
                <Box component="ol" sx={{ mt: 1, pl: 2 }}>
                  <li>{t("recorders.deleteStep1")}</li>
                  <li>{t("recorders.deleteStep2", { count: associatedCamerasCount })}</li>
                  <li>{t("recorders.deleteStep3")}</li>
                </Box>
              </Box>
            )}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {associatedCamerasCount > 0 && recorderToDelete && (
            <Button
              onClick={() => {
                handleCloseWarning();
                handleViewRecorderCameras(recorderToDelete.recorderId);
              }}
              variant="contained"
              color="primary"
            >
              {t("recorders.viewCameras")}
            </Button>
          )}
          <Button onClick={handleCloseWarning} variant="outlined">
            {t("recorders.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Detalhes do Gravador */}
      <Dialog
        open={openDetailsDialog}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        {selectedRecorder && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: { xs: 2, sm: 3 },
              }}
            >
              <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                {selectedRecorder.recorderName}
              </Typography>
              <IconButton edge="end" color="inherit" onClick={handleCloseDetails} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                    {t("recorders.basicInfo")}
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("recorders.fieldId")}</strong> {selectedRecorder.recorderId}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("recorders.fieldName")}</strong> {selectedRecorder.recorderName}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <strong>{t("recorders.fieldType")}</strong>
                      <Chip
                        label={getRecorderTypeName(selectedRecorder.recorderType)}
                        color={getRecorderTypeColor(selectedRecorder.recorderType)}
                        size="small"
                        variant="outlined"
                      />
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("recorders.fieldModel")}</strong> {selectedRecorder.recorderModel || t("recorders.modelNotSpecified")}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      <strong>{t("recorders.fieldChannels")}</strong> {selectedRecorder.recorderChannels}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
  <strong>{t("recorders.fieldStatus")}</strong>
  <Chip
    label={getRecorderStatusLabel(selectedRecorder.recorderStatus)}
    color={getRecorderStatusColor(selectedRecorder.recorderStatus)}
    size="small"
    sx={{ ml: 1 }}
  />
</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                    {t("recorders.networkConfig")}
                  </Typography>
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" }, mt: 1 }}>
                      <strong>{t("recorders.internalNetwork")}</strong>
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      IP: {selectedRecorder.recorderIntIP}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      HTTP: {selectedRecorder.recorderIntHTTP} | RTSP: {selectedRecorder.recorderIntRTSP}
                    </Typography>
                    
                    <Typography variant="subtitle2" sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" }, mt: 2 }}>
                      <strong>{t("recorders.externalNetwork")}</strong>
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      IP: {selectedRecorder.recorderExtIP || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                      HTTP: {selectedRecorder.recorderExtHTTP || "-"} | RTSP: {selectedRecorder.recorderExtRTSP || "-"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: isMobile ? "column" : "row" }}>
              <Button
                variant="contained"
                startIcon={<VisibilityIcon />}
                onClick={() => {
                  handleCloseDetails();
                  handleViewRecorderCameras(selectedRecorder.recorderId);
                }}
                sx={{ mb: isMobile ? 1 : 0, mr: isMobile ? 0 : 1, width: isMobile ? "100%" : "auto" }}
              >
                {t("recorders.viewCameras")}
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  handleCloseDetails();
                  handleEdit(selectedRecorder.recorderId);
                }}
                sx={{ mb: isMobile ? 1 : 0, mr: isMobile ? 0 : 1, width: isMobile ? "100%" : "auto" }}
              >
                {t("recorders.edit")}
              </Button>
              
              <Button
                variant="outlined"
                color={selectedRecorder.recorderStatus === 1 ? "warning" : "success"}
                onClick={() => handleToggleStatus(selectedRecorder)}
                sx={{ mb: isMobile ? 1 : 0, mr: isMobile ? 0 : 1, width: isMobile ? "100%" : "auto" }}
              >
                {selectedRecorder.recorderStatus === 1 ? t("recorders.deactivate") : t("recorders.activate")}
              </Button>

              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  handleCloseDetails();
                  handleDeleteWithValidation(selectedRecorder.recorderId);
                }}
                sx={{ width: isMobile ? "100%" : "auto" }}
              >
                {t("recorders.delete")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}

export default RecorderList